import {
  CharacterSchema,
  CHARACTER_SCHEMA_VERSION,
  type Character,
  type ChosenAspetto,
  type MoveRef,
  type SpellRef,
} from '../schemas/character';
import type { AlignmentChoice } from '../schemas/alignment';
import type { Bond } from '../schemas/bond';
import type { ClassDefinition, DamageDie } from '../schemas/classDefinition';
import type { Item } from '../schemas/equipment';
import type { RaceChoice } from '../schemas/race';
import { scoreToModifier, type Stats } from '../schemas/stats';
import type { AcquiredMove } from '../schemas/moveAcquisition';
import { findMoveByIdGlobal } from '../data/moves';
import { getItemTotalWeight } from '../utils/getItemTotalWeight';
import { ANCHORS_REQUIRING_BUNDLE_EXPANSION } from './bundle';
import { getFollowersFromDraft } from './followerCreation';
import { applyMoveAcquisition } from './moveAcquisition';

/**
 * **FI-38 Opz. C (2026-05-14)** + **FI-81 #1 (2026-05-24)**: applica
 * eventuale `grantsMoveChoice from-choice-mapping` cross-class su una
 * mossa starting. Caso d'uso unico oggi: `paladino.ordine-sovrannaturale`
 * che concede una mossa Warlock (Catena/Tomo/Lama) in base alla
 * `selectedChoices` del PG.
 *
 * **FI-81 #1**: se la mossa concessa ha `hasMoveSetup` (es.
 * `warlock.vincolo-della-lama` con tipologia/portata/2 miglioramenti/
 * descrizione), il setup viene letto da `allDraftMoves` cercando il
 * MoveRef della mossa concessa già popolato dal builder L1.
 * Fallback a stub `{ selectedChoices: [] }` per retro-compat (mosse
 * concesse description-only o builder pre-FI-81 senza UI di setup).
 *
 * Se la mossa concessa ha a sua volta `bundles` (es. `warlock.vincolo-del-tomo`),
 * `applyMoveAcquisition` con `expandBundle: true` propaga automaticamente
 * il bundle.
 *
 * **No-op** se la mossa non ha `grantsMoveChoice from-choice-mapping`,
 * non ha selectedChoices, o la choice non è nel mapping.
 */
function applyStartingGrantedChoice(
  character: Character,
  moveRef: MoveRef,
  classDef: ClassDefinition,
  allDraftMoves: readonly MoveRef[],
): Character {
  const moveDef = findMoveByIdGlobal(moveRef.id);
  if (moveDef?.grantsMoveChoice?.kind !== 'from-choice-mapping') {
    return character;
  }
  const selectedChoice = moveRef.selectedChoices[0];
  if (selectedChoice === undefined) return character;
  const grantedMoveId = moveDef.grantsMoveChoice.mapping[selectedChoice];
  if (grantedMoveId === undefined) return character;
  // **FI-81 #1**: cerca il setup compilato dal builder L1 se presente.
  const grantedFromDraft = allDraftMoves.find((m) => m.id === grantedMoveId);
  const grantedMoveRef: MoveRef = grantedFromDraft ?? {
    id: grantedMoveId,
    selectedChoices: [],
  };
  const grantedAcquired: AcquiredMove = {
    moveRef: grantedMoveRef,
    source: 'granted',
    spellsSelected: [],
  };
  return applyMoveAcquisition(character, grantedAcquired, classDef, { expandBundle: true });
}

/**
 * Risultato del calcolo dei valori derivati per un PG di livello 1.
 *
 * I campi memorizzati sulla scheda PG (`hp.max`, `load.max`, `damage`,
 * `armor`) sono derivati dalle scelte di creazione (classe, statistiche,
 * inventario). Vengono calcolati al momento del finalize del builder e
 * memorizzati sul `Character` per autosufficienza della scheda nei modi
 * "stampabile" e "import/export".
 */
export interface StartingDerivedStats {
  hp: { current: number; max: number };
  load: { current: number; max: number };
  damage: DamageDie;
  armor: number;
}

/**
 * Calcola il `baseHp` effettivo del PG considerando eventuali override
 * portati dagli archetipi.
 *
 * **Regola FI-1 (creazione) + FI-1 esteso (level-up via Infanzia Complessa)**:
 * `Archetype.baseHpOverride`, se definito, sostituisce `classDef.baseHp`.
 * Caso noto: Paladino-Nano → `baseHpOverride: 12` (invece di 10).
 *
 * La regola si applica indipendentemente da QUANDO l'archetipo è stato
 * scelto: alla creazione (FI-1 originale) o al level-up come 2° archetipo
 * via *Infanzia Complessa* (FI-1 esteso, D1). Il chiamante passa entrambi
 * gli archetype-id; se entrambi hanno `baseHpOverride`, vince il **più
 * alto** (regola difensiva: nelle 12 classi DWR esiste 1 solo archetipo
 * con override — Paladino-Nano — quindi il caso "conflitto" è teorico).
 *
 * @param classDef definizione della classe del PG
 * @param archetypeId archetipo primario (sempre presente sul `Character`)
 * @param secondaryArchetypeId opzionale, popolato post-IC al level-up
 */
export function getEffectiveBaseHp(
  classDef: ClassDefinition,
  archetypeId: string,
  secondaryArchetypeId?: string,
): number {
  const primary = classDef.archetypes.find((a) => a.id === archetypeId);
  const secondary = secondaryArchetypeId
    ? classDef.archetypes.find((a) => a.id === secondaryArchetypeId)
    : undefined;
  const primaryHp = primary?.baseHpOverride ?? classDef.baseHp;
  const secondaryHp = secondary?.baseHpOverride ?? classDef.baseHp;
  return Math.max(primaryHp, secondaryHp);
}

/**
 * **FI-45 C5** — SSOT per il calcolo di HP max.
 *
 * Formula: `hpMax = effectiveBaseHp + COS_PUNTEGGIO` (manuale base DWR
 * r. 1800). `effectiveBaseHp` deriva da `getEffectiveBaseHp` (applica
 * `baseHpOverride` di archetipo primario/secondario, vince il più alto).
 *
 * Usato da:
 *  - `deriveStartingDerivedStats` (creazione PG L1)
 *  - `recomputeHp` in `services/levelUp.ts` (level-up con stat-increase
 *    di COS o cambio archetipo via *Infanzia Complessa*)
 *
 * **`archetypeId` opzionale**: in scenari di tooling pre-archetype-pick
 * (preview builder) può mancare, in tal caso si usa `classDef.baseHp`.
 * In runtime su `Character` consolidato è sempre presente.
 */
export function computeMaxHp(
  classDef: ClassDefinition,
  costituzione: number,
  archetypeId: string | undefined,
  secondaryArchetypeId: string | undefined,
): number {
  const effectiveBaseHp = archetypeId
    ? getEffectiveBaseHp(classDef, archetypeId, secondaryArchetypeId)
    : classDef.baseHp;
  return effectiveBaseHp + costituzione;
}

/**
 * Calcola tutti i valori derivati per un PG di livello 1.
 *
 * **Formule** (manuale base DWR):
 *  - `hp.max = effectiveBaseHp + COS_PUNTEGGIO` (manuale r. 1800 — il
 *    punteggio, NON il modificatore di Costituzione). `effectiveBaseHp` è
 *    calcolato da `getEffectiveBaseHp` (applica `baseHpOverride` se presente).
 *  - `hp.current = hp.max` (PG appena creato)
 *  - `load.max = classDef.baseLoad + FOR_MODIFICATORE` (manuale r. 1806 —
 *    il modificatore, NON il punteggio di Forza)
 *  - `load.current = sum(weight × quantity)` di tutto l'inventario
 *  - `damage = classDef.damageDie`
 *  - `armor = max(armor)` di tutti gli item (vedi `computeStartingArmor`)
 *
 * **Trappola memorizzabile**: PUNTEGGIO per HP, MODIFICATORE per Carico.
 * È una distinzione su cui ci si può sbagliare a memoria; il commento
 * resta qui perché è una regola di sistema non ovvia.
 *
 * **FI-1 + FI-1 esteso (D1)**: `archetypeId` obbligatorio (alla creazione
 * il PG ha sempre un archetipo). Il `secondaryArchetypeId` opzionale è
 * accettato per uniformità della firma con il level-up (`applyLevelUp`),
 * anche se in creazione è sempre `undefined`.
 */
export function deriveStartingDerivedStats(
  classDef: ClassDefinition,
  stats: Stats,
  inventory: Item[],
  archetypeId?: string,
  secondaryArchetypeId?: string,
): StartingDerivedStats {
  const hpMax = computeMaxHp(classDef, stats.costituzione, archetypeId, secondaryArchetypeId);
  const loadMax = classDef.baseLoad + scoreToModifier(stats.forza);
  const loadCurrent = computeStartingLoad(inventory);
  const armor = computeStartingArmor(inventory);

  return {
    hp: { current: hpMax, max: hpMax },
    load: { current: loadCurrent, max: loadMax },
    damage: classDef.damageDie,
    armor,
  };
}

/**
 * Carico totale dell'inventario di partenza, con quantity-aware sum.
 * Esposto per test (e per uso futuro nella scheda live).
 */
export function computeStartingLoad(inventory: Item[]): number {
  return inventory.reduce((sum, item) => sum + getItemTotalWeight(item), 0);
}

/**
 * Armor totale dell'equipaggiamento di partenza.
 *
 * **MVP semplificato** (livello 1, 3 classi non casteggianti): prendiamo
 * il massimo `armor` tra tutti gli item invece di sommare. Per le 3 classi
 * attuali non esistono casi cumulativi reali — ognuna sceglie al massimo
 * un'armatura indossata.
 *
 * **Caso non coperto**: lo scudo del Guerriero descrive il bonus "+1
 * Armatura quando usato" nella `description` testuale, senza un campo
 * numerico `armor`. La logica di computo lo ignora — annotato nel
 * gap report del MVP per una soluzione strutturata futura.
 */
export function computeStartingArmor(inventory: Item[]): number {
  return inventory.reduce((max, item) => Math.max(max, item.armor ?? 0), 0);
}

/**
 * Input completo richiesto per finalizzare un PG di livello 1.
 *
 * Le proprietà ricalcano i campi del `Character` ma sono limitate a
 * quanto il giocatore decide nel builder. I campi derivati (`hp`, `load`,
 * `damage`, `armor`) e quelli automatici (`id`, `level: 1`, `xp: 0`,
 * `createdAt`, `updatedAt`, `schemaVersion`) NON sono nell'input.
 */
export interface FinalizeCharacterInput {
  name: string;
  // exactOptionalPropertyTypes: true → opzionali con `| undefined` esplicito
  title?: string | undefined;
  classDef: ClassDefinition;
  archetypeId: string;
  race: RaceChoice;
  alignment: AlignmentChoice;
  stats: Stats;
  chosenAspetti: ChosenAspetto[];
  moves: MoveRef[];
  spells: SpellRef[];
  inventory: Item[];
  bonds: Bond[];
  /**
   * Se `true`, il giocatore ha posticipato la compilazione dei legami alla
   * prima sessione (FI-25). Default `undefined` = legami compilati alla
   * creazione.
   */
  bondsDeferred?: boolean | undefined;
  notes?: string | undefined;
}

/**
 * **FI-46bis** — Overrides opzionali per preservare l'identità del PG
 * in scenari di edit retroattivo L1. In creazione normale entrambi i
 * campi sono undefined → genera nuovi (UUID + now).
 */
export interface FinalizeCharacterOverrides {
  /** Se passato, usa questo come `Character.id` invece di generarne uno nuovo. */
  id?: string | undefined;
  /** Se passato, usa questo come `Character.createdAt` invece di now(). */
  createdAt?: string | undefined;
}

/**
 * Trasforma l'input completo del builder in un `Character` valido di
 * livello 1, eseguendo il calcolo dei derivati e la validazione finale
 * via `CharacterSchema.parse()`.
 *
 * **Effetti**:
 *  - genera un `id` UUID nuovo (o usa `overrides.id` se passato — FI-46bis)
 *  - imposta `level: 1`, `xp: 0`
 *  - copia `input.spells` su `Character.spells` (incluso array vuoto per
 *    PG non casteggianti / senza mosse di casting)
 *  - timestamp `createdAt`/`updatedAt` = ora (o `overrides.createdAt` se
 *    passato — FI-46bis preserva l'identità del PG in edit retroattivo)
 *  - calcola `hp`, `load`, `damage`, `armor` da `deriveStartingDerivedStats`
 *  - copia `damageDie` su `damage` (per autosufficienza scheda)
 *
 * Lancia se il parse Zod fallisce (sentinel di robustezza: in teoria con
 * input ben tipato non dovrebbe mai accadere, ma il `parse` finale
 * cattura eventuali invarianti cross-field non tenute dal tipo TS).
 */
export function finalizeCharacter(
  input: FinalizeCharacterInput,
  overrides: FinalizeCharacterOverrides = {},
): Character {
  const derived = deriveStartingDerivedStats(
    input.classDef,
    input.stats,
    input.inventory,
    input.archetypeId,
  );
  const now = new Date().toISOString();

  // **FI-35 (D-FI35)**: il `Character.moves` e `Character.spells` finali
  // vengono costruiti **incrementalmente** chiamando `applyMoveAcquisition`
  // per ogni mossa selezionata dal builder. Questo applica unitariamente
  // gli effetti collaterali di ogni mossa: replaces, spell-grants auto
  // (es. 3 trucchetti Mago, tutte Suppliche Warlock, fixed-list).
  //
  // Le spell scelte dal giocatore (`input.spells`) vengono aggiunte alla
  // fine come "pick", dedupedo contro le spell già accumulate dalle auto-
  // grants — la regressione UX è zero perché `SectionSpellSelection`
  // pre-popola gli auto in `input.spells` (la stessa spell non viene
  // duplicata grazie al dedup).
  //
  // Ordine: `input.moves` rappresenta le mosse selezionate dal builder
  // (startingMoves auto + scelte gruppi + mossa-archetipo). `applyMoveAcquisition`
  // viene chiamata SENZA `spellsSelected` per ogni mossa: le spell pick
  // del giocatore vengono dal flusso UI separato (sezione `spells`).
  const baseCharacter: Character = {
    schemaVersion: CHARACTER_SCHEMA_VERSION,
    id: overrides.id ?? crypto.randomUUID(),
    name: input.name,
    title: input.title,
    classId: input.classDef.id,
    archetypeId: input.archetypeId,
    level: 1,
    xp: 0,
    race: input.race,
    alignment: input.alignment,
    stats: input.stats,
    chosenAspetti: input.chosenAspetti,
    hp: derived.hp,
    armor: derived.armor,
    damage: derived.damage,
    load: derived.load,
    moves: [],
    spells: [],
    inventory: input.inventory,
    bonds: input.bonds,
    bondsDeferred: input.bondsDeferred,
    followers: [],
    levelUpHistory: [],
    notes: input.notes ?? '',
    createdAt: overrides.createdAt ?? now,
    updatedAt: now,
  };

  let accumulator: Character = baseCharacter;
  for (const moveRef of input.moves) {
    const acquired: AcquiredMove = {
      moveRef,
      source: 'main',
      spellsSelected: [],
    };
    // **D7-bundle + FI-38 Opz. C**: builder L1 disabilita l'espansione
    // bundle di default. Le mosse iniziali del bundle Mago/Chierico
    // (`corpus-arcano` + `mana` + `lanciare-incantesimi-arcani` per il
    // Mago; `divinita` + `mana` + `lanciare-incantesimi-divini` per il
    // Chierico) sono già tutte esplicite in `input.moves` con setup
    // completato dal giocatore — espandere il bundle qui rischierebbe di
    // applicare mosse senza setup in base all'ordine di iterazione.
    //
    // **Anchor che richiedono bundle expansion al builder L1**: anchor
    // le cui mosse del bundle NON sono in `input.moves` (non sono AUTO
    // né CHOICE — sono bundle-obtained quando il giocatore seleziona
    // l'anchor):
    //  - `warlock.vincolo-del-tomo` → bundle [`warlock.mana`, `warlock.LI-del-Patrono`]
    //  - `druido.divinita-della-natura` → bundle cross-class [`chierico.LI-Divini`, `chierico.mana`]
    //  - `paladino.ordine-sacro` → bundle cross-class idem Druido
    const expandBundle = ANCHORS_REQUIRING_BUNDLE_EXPANSION.has(moveRef.id);
    accumulator = applyMoveAcquisition(accumulator, acquired, input.classDef, {
      expandBundle,
    });
    // **FI-38 Opz. C — `grantsMoveChoice from-choice-mapping` su starting moves**:
    // se la mossa ha grantsMoveChoice cross-class (es. Paladino-Sovrannaturale
    // che concede un Vincolo Warlock), risolvi la choice e acquisisci
    // la mossa cross-class come `source: 'granted'`. Il bundle dell'eventuale
    // mossa concessa (es. Vincolo del Tomo) viene espanso automaticamente.
    accumulator = applyStartingGrantedChoice(accumulator, moveRef, input.classDef, input.moves);
  }

  // Aggiungi le spell pick del giocatore (dedup contro auto-grants già applicate)
  const existingSpellIds = new Set(accumulator.spells.map((s) => s.id));
  const pickedSpells = input.spells.filter((s) => !existingSpellIds.has(s.id));
  accumulator = { ...accumulator, spells: [...accumulator.spells, ...pickedSpells] };

  // FI-8a: Follower derivati dalle mosse-source con `followerTemplateId`
  // e setup completo. Congelati in `Character.followers` al finalize.
  const followers = getFollowersFromDraft(input.classDef.id, accumulator.moves, 1);
  accumulator = { ...accumulator, followers };

  return CharacterSchema.parse(accumulator);
}
